
import { User } from '@supabase/supabase-js';
import { UserProfile } from '@/types/user';

/**
 * Gets display name for a user, prioritizing user_profiles data over auth metadata
 */
export const getUserDisplayName = (
  user: User | null, 
  userProfile: UserProfile | null
): string => {
  if (!user) return 'User';
  
  // Check user_profiles data first
  if (userProfile) {
    const firstName = userProfile.firstname || '';
    const lastName = userProfile.lastname || '';
    if (firstName || lastName) return `${firstName} ${lastName}`.trim();
  }
  
  // Fall back to user metadata
  const metadata = user.user_metadata;
  if (!metadata) return user.email?.split('@')[0] || 'User';
  
  if (metadata.full_name) return metadata.full_name;
  
  const firstName = metadata.first_name || '';
  const lastName = metadata.last_name || '';
  if (firstName || lastName) return `${firstName} ${lastName}`.trim();
  
  return user.email?.split('@')[0] || 'User';
};

/**
 * Gets user initials, prioritizing user_profiles data over auth metadata
 */
export const getUserInitials = (
  user: User | null, 
  userProfile: UserProfile | null
): string => {
  // Check user_profiles data first
  if (userProfile) {
    const firstName = userProfile.firstname || '';
    const lastName = userProfile.lastname || '';
    
    if (firstName && lastName) {
      return firstName.charAt(0).toUpperCase() + lastName.charAt(0).toUpperCase();
    }
    
    if (firstName) {
      return firstName.charAt(0).toUpperCase();
    }
    
    if (lastName) {
      return lastName.charAt(0).toUpperCase();
    }
  }
  
  // Fall back to user metadata
  if (!user || !user.user_metadata) {
    return user?.email?.charAt(0).toUpperCase() || 'U';
  }
  
  const fullName = user.user_metadata.full_name;
  if (typeof fullName !== 'string' || !fullName.trim()) {
    return user.email?.charAt(0).toUpperCase() || 'U';
  }
  
  const nameParts = fullName.trim().split(/\s+/);
  if (nameParts.length === 1) {
    return nameParts[0].charAt(0).toUpperCase();
  }
  return nameParts[0].charAt(0).toUpperCase() + nameParts[nameParts.length - 1].charAt(0).toUpperCase();
};

/**
 * Determines if a user has access to admin dashboard based on role
 */
export const hasAdminDashboardAccess = (
  userRole: string | null | undefined,
  isBusinessOwner: boolean | undefined
): boolean => {
  // Only admins and reviewers can access the dashboard
  return (userRole === 'admin' || userRole === 'superadmin' || userRole === 'reviewer');
};

